// limitDamagePremRented exists on the base application object, not in the endorsements object.
// limitDamagePremRented does not come back from the available endorsements endpoint.

import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { NumberFormatValues } from 'react-number-format';
import styled from 'styled-components';
import { setLimitDamagePremRented, setCheckedEndorsement } from '../../../../actions/application';
import { selectLimitDamagePremRented, selectCheckedEndorsements } from '../../../../selectors/application';
import { selectLoadingState } from '../../../../selectors';
import { LabelLarge } from '../../../../elements';
import EndorsementCard from '../../EndorsementCard';
import CenteredLoadingSpinner from '../../../AddOns/shared/CenteredLoadingSpinner';
import {
  SubQuestionCont,
  UnderwritingQuestionCont,
  UnderWritingNumberInput,
  StyledInputErrorMessage,
} from '../../styles/endorsements';
import { config } from './DamagesToPremisesRented.config';

const { endorsementKey: ENDORSEMENT_KEY, label: LABEL, description: DESCRIPTION } = config;

const validateInput = (value: number) => value >= 50_000 && value <= 1_000_000;

const DamagesToPremisesRented = () => {
  const dispatch = useDispatch();
  const limitDamagePremRented = useSelector(selectLimitDamagePremRented);
  const isQuoteLoading = useSelector(selectLoadingState);
  const isSelected = useSelector(selectCheckedEndorsements).includes(ENDORSEMENT_KEY);
  const [isValid, setIsValid] = useState(true);
  const [localLimitDamagePremRented, setLocalLimitDamagePremRented] = useState(limitDamagePremRented);

  const handleCheckboxChange = (checked: boolean) => {
    dispatch(setCheckedEndorsement(ENDORSEMENT_KEY, checked));
    // Checking the box dispatches {} which the reducer converts to the $50k baseline.
    dispatch(setLimitDamagePremRented(checked ? {} : null));
  };

  const handleValueChange = (values: NumberFormatValues) => {
    setIsValid(true);
    setLocalLimitDamagePremRented(values.floatValue ?? undefined);
  };

  const handleBlur = () => {
    if (localLimitDamagePremRented != null) {
      setIsValid(validateInput(localLimitDamagePremRented));
      dispatch(setLimitDamagePremRented({ limitDamagePremRented: localLimitDamagePremRented }));
    }
  };

  return (
    <EndorsementCard
      endorsementKey={ENDORSEMENT_KEY}
      label={LABEL}
      description={DESCRIPTION}
      isSelected={isSelected}
      onCheckboxChange={handleCheckboxChange}
      hasExpandableContent
    >
      <SubQuestionsContainer>
        {isQuoteLoading && <CenteredLoadingSpinner useAltColor />}
        <SubQuestionCont isActive={!isQuoteLoading} direction="column">
          <UnderwritingQuestionCont>
            <StyledLabelLarge htmlFor="limitDamagePremRented">
              Choose your total coverage amount up to $1,000,000
            </StyledLabelLarge>
            <UnderWritingNumberInput
              name="limitDamagePremRented"
              value={limitDamagePremRented}
              onValueChange={handleValueChange}
              onBlur={handleBlur}
              customwidth="quarter"
              marginbottom="none"
              thousandSeparator={true}
              prefix="$"
              aria-label="Choose your total coverage amount up to $1,000,000"
              data-cy="limitDamagePremRented"
              disabled={isQuoteLoading}
              hasError={!isValid}
              placeholder="e.g., $150,000"
            />
          </UnderwritingQuestionCont>
          {!isValid && (
            <StyledInputErrorMessage>
              An increased limit amount must be greater than $50,000 and less than $1,000,000
            </StyledInputErrorMessage>
          )}
        </SubQuestionCont>
      </SubQuestionsContainer>
    </EndorsementCard>
  );
};

const SubQuestionsContainer = styled.div`
  position: relative;
`;

const StyledLabelLarge = styled(LabelLarge)`
  align-self: center;
`;

export default DamagesToPremisesRented;
